/**
 * Helpers para convertir entre los formatos de "config persistido" (DataExplorerView._config,
 * snake_case) y "filters" que usa el DataExplorerHeader / encodeFiltersToUrl (camelCase).
 *
 * Para cargar una vista en el explorador:
 *   const filters = viewToFilters(view);
 *   const url = encodeFiltersToUrl(filters) + `&view_id=${view.id}`;
 *   window.location.href = url;
 *
 * El Header decodea via decodeFiltersFromUrl() en componentDidMount → aplica filters.
 * DataExplorerPage detecta view_id en URL y muestra el banner editorial.
 */

import { encodeFiltersToUrl } from './DataExplorerUrlParams';

// Fields auto-inyectados por DataExplorerHeader.getDimensionsFiltersForPost() a partir de
// selectedItems + sourceType. Nunca deberían aparecer como chips visibles ni viajar en el
// config persistido. Cubren tanto vistas nuevas como vistas viejas guardadas pre-fix.
const AUTO_INJECTED_FILTER_FIELDS = new Set(['Presupuesto_ID', 'Canal', 'Cuenta_ID']);

function stripAutoInjectedFilters(rawGroups) {
    if (!Array.isArray(rawGroups)) return [];
    const cleaned = [];
    rawGroups.forEach((group) => {
        if (Array.isArray(group)) {
            const inner = group.filter(
                (f) => f && !AUTO_INJECTED_FILTER_FIELDS.has(f.fieldName)
            );
            if (inner.length > 0) cleaned.push(inner);
        } else if (group && !AUTO_INJECTED_FILTER_FIELDS.has(group.fieldName)) {
            cleaned.push(group);
        }
    });
    return cleaned;
}

export function deserializePeriod(period) {
    if (!period) return {};
    if (period.type === 'FIXED' && period.start_date && period.end_date) {
        return {
            selectedPeriod: 'CUSTOM',
            range: {
                from: new Date(period.start_date),
                to: new Date(period.end_date),
            },
        };
    }
    if (period.type === 'RELATIVE' && period.predefined_period) {
        return { selectedPeriod: period.predefined_period };
    }
    return {};
}

export function viewToFilters(view) {
    if (!view || !view.config) return null;
    const config = view.config;
    const filters = {
        agencyClientId: view.agency_client_id,
        sourceType: config.source_type,
        selectedItems: config.selected_items || [],
        periodState: deserializePeriod(config.period),
        compare: !!config.compare,
        dimensions: config.dimensions || [],
        metrics: (config.metrics || []).map((m) => (typeof m === 'string' ? m : m.name)),
        dimensionsFilters: stripAutoInjectedFilters(config.dimensions_filters || []),
    };

    if (config.compare && config.compare_period) {
        if (config.compare_period.type === 'FIXED') {
            filters.comparePeriod = 'CUSTOM';
            filters.compareRange = {
                from: new Date(config.compare_period.start_date),
                to: new Date(config.compare_period.end_date),
            };
        } else if (config.compare_period.type === 'RELATIVE') {
            filters.comparePeriod = config.compare_period.predefined_period || 'PREVIOUS_PERIOD';
        }
    }

    return filters;
}

/**
 * Construye la URL para "cargar" una vista en el explorador.
 * Aggrega `view_id=ID` al URL generado por encodeFiltersToUrl para que la página
 * detecte la vista cargada y muestre el banner editorial.
 */
export function buildLoadViewUrl(view) {
    const filters = viewToFilters(view);
    if (!filters) return null;
    const baseUrl = encodeFiltersToUrl(filters);
    const url = new URL(baseUrl);
    // encodeFiltersToUrl hereda el pathname de window.location → si "Abrir" se clickea
    // desde otro path (ej. /client/:id/portal-config), la URL nunca llevaría al Explorador.
    // Forzamos el path al Data Explorer para que siempre se cargue ahí.
    url.pathname = '/data-explorer';
    url.searchParams.set('view_id', String(view.id));
    return url.toString();
}

/**
 * Construye la URL para "replicar" una vista de OTRO cliente en el cliente actual,
 * como BORRADOR sin guardar (Card #2420).
 *
 * Copia lo portable de la vista (source_type, dimensiones, métricas, período, compare,
 * dimensions_filters) pero:
 *   - fuerza `agencyClientId` al cliente actual,
 *   - limpia `selectedItems` (las líneas/cuentas pertenecen al cliente origen y no existen
 *     acá — `renderItemSelector` solo muestra los del cliente actual),
 *   - NO agrega `view_id` → es un borrador, no dispara el banner de vista cargada.
 *
 * Con selección vacía el Explorador busca sobre TODAS las líneas/cuentas del cliente actual
 * (ver `canPerformSearch` + `getDimensionsFiltersForPost`), así que aterriza mostrando el
 * mismo reporte al instante. `chart_preferences` se persiste aparte en LocalStorage por el
 * caller antes de navegar (no viaja en el blob de filtros).
 */
export function buildReplicateDraftUrl(view, currentClientId) {
    const filters = viewToFilters(view);
    if (!filters || !currentClientId) return null;
    filters.agencyClientId = String(currentClientId);
    filters.selectedItems = [];
    const url = new URL(encodeFiltersToUrl(filters));
    url.pathname = '/data-explorer';
    if (view.name) url.searchParams.set('replicated_from', view.name);
    return url.toString();
}

/**
 * Lee el nombre de la vista origen de una replicación (set por buildReplicateDraftUrl).
 * Se usa para el toast de aterrizaje y para pre-cargar el nombre en "Guardar como vista".
 */
export function readReplicatedFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('replicated_from');
    return raw ? raw.trim() : null;
}

/**
 * Lee el view_id del URL actual (set por buildLoadViewUrl).
 */
export function readViewIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('view_id');
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
}
